import { useState, useMemo, useEffect, Fragment } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAccounting } from "@/contexts/AccountingContext";
import { useAuth } from "@/contexts/AuthContext";
import { VoucherType, Voucher, VoucherStatus } from "@/lib/accounting/types";
import { ContraVoucherForm } from "@/components/accounting/ContraVoucherForm";
import { PaymentVoucherForm } from "@/components/accounting/PaymentVoucherForm";
import { ReceiptVoucherForm } from "@/components/accounting/ReceiptVoucherForm";
import { VoucherEntryForm } from "@/components/accounting/VoucherEntryForm";
import { GSTInvoiceForm } from "@/components/accounting/GSTInvoiceForm";
import { InventoryVoucherForm } from "@/components/accounting/InventoryVoucherForm";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowRightLeft,
  CreditCard,
  IndianRupee,
  BookOpen,
  Plus,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  XCircle,
  Trash2,
  ChevronDown,
  ChevronRight,
  Eye,
  FileText,
  RotateCcw,
  Calendar,
  Layers,
  Truck,
  Package,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type TabType =
  | "all"
  | "contra"
  | "payment"
  | "receipt"
  | "journal"
  | "sales"
  | "purchase"
  | "credit-note"
  | "debit-note"
  | "stock-journal"
  | "delivery-note"
  | "receipt-note"
  | "physical-stock";

const STATUS_ICONS: Record<VoucherStatus, { icon: any; className: string }> = {
  Approved: { icon: CheckCircle2, className: "text-success bg-success/10 border-success/20" },
  Pending: { icon: Clock, className: "text-warning bg-warning/10 border-warning/20" },
  Rejected: { icon: XCircle, className: "text-destructive bg-destructive/10 border-destructive/20" },
  Draft: { icon: Clock, className: "text-muted-foreground bg-muted border-border" },
  Cancelled: { icon: Trash2, className: "text-muted-foreground bg-muted/60 border-border line-through" },
};

export default function JournalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { vouchers, ledgers, cancelVoucher, approveVoucher, rejectVoucher } = useAccounting();
  const { role } = useAuth();
  const { toast } = useToast();

  // Determine tab from pathname or URL query parameter ?type=
  const pathname = window.location.pathname.toLowerCase();
  let defaultType: TabType = "all";
  if (pathname.includes("credit-note")) defaultType = "credit-note";
  else if (pathname.includes("debit-note")) defaultType = "debit-note";
  else if (pathname.includes("stock-adjustment") || pathname.includes("stock-journal")) defaultType = "stock-journal";
  else if (pathname.includes("/dashboard/contra")) defaultType = "contra";
  else if (pathname.includes("/dashboard/payment")) defaultType = "payment";
  else if (pathname.includes("/dashboard/receipt")) defaultType = "receipt";

  const typeParam = (searchParams.get("type") || (defaultType !== "all" ? defaultType : "all")).toLowerCase().replace(/[\s_]/g, "-") as TabType;
  const [activeTab, setActiveTab] = useState<TabType>(typeParam || "all");

  // Keep state and URL query param synchronized
  useEffect(() => {
    if (typeParam && typeParam !== activeTab) {
      setActiveTab(typeParam);
    }
  }, [typeParam]);

  const handleTabChange = (val: string) => {
    const tab = val as TabType;
    setActiveTab(tab);
    if (tab === "all") {
      searchParams.delete("type");
    } else {
      searchParams.set("type", tab);
    }
    setSearchParams(searchParams);
  };

  // Dedicated modal open states
  const [contraOpen, setContraOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);
  const [stockJournalOpen, setStockJournalOpen] = useState(false);

  // Auto-open modal if ?action=new or ?new=true
  useEffect(() => {
    const isNew = searchParams.get("action") === "new" || searchParams.get("new") === "true";
    if (isNew) {
      if (typeParam === "contra") setContraOpen(true);
      else if (typeParam === "payment") setPaymentOpen(true);
      else if (typeParam === "receipt") setReceiptOpen(true);
      else if (typeParam === "journal") setJournalOpen(true);
      else if (typeParam === "stock-journal") setStockJournalOpen(true);
      // Clean up param
      searchParams.delete("action");
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [typeParam, searchParams, setSearchParams]);

  // Filtering states
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<VoucherStatus | "All">("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Cancellation modal state
  const [cancellingVoucher, setCancellingVoucher] = useState<Voucher | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // View Details Modal
  const [viewVoucher, setViewVoucher] = useState<Voucher | null>(null);

  // Filter vouchers
  const filteredVouchers = useMemo(() => {
    return vouchers.filter((v) => {
      // 1. Tab filter
      if (activeTab !== "all") {
        const normTab = activeTab.replace(/[\s\-_]/g, "");
        const normType = v.type.toLowerCase().replace(/[\s\-_]/g, "");
        if (normTab !== normType) return false;
      }

      // 2. Status filter
      if (statusFilter !== "All" && v.status !== statusFilter) return false;

      // 3. Date range filter
      if (startDate && v.date < startDate) return false;
      if (endDate && v.date > endDate) return false;

      // 4. Search query
      if (search.trim()) {
        const q = search.toLowerCase().trim();
        const numMatch = v.voucherNumber.toLowerCase().includes(q);
        const partyMatch = v.partyName?.toLowerCase().includes(q) || false;
        const narrMatch = v.narration.toLowerCase().includes(q);
        const invMatch = v.invoiceNumber?.toLowerCase().includes(q) || false;
        const instMatch = v.instrumentNo?.toLowerCase().includes(q) || false;
        const linesMatch = v.lines.some((l) => l.ledgerName.toLowerCase().includes(q));
        if (!numMatch && !partyMatch && !narrMatch && !invMatch && !instMatch && !linesMatch) {
          return false;
        }
      }

      return true;
    });
  }, [vouchers, activeTab, statusFilter, startDate, endDate, search]);

  const handleCancelSubmit = async () => {
    if (!cancellingVoucher) return;
    setCancelling(true);
    try {
      const res = await cancelVoucher(cancellingVoucher.id, cancelReason.trim() || "Cancelled by user");
      if (res.success) {
        toast({ title: "Voucher Cancelled", description: `${cancellingVoucher.voucherNumber} cancelled. Ledger postings safely reversed.` });
        setCancellingVoucher(null);
        setCancelReason("");
        if (viewVoucher?.id === cancellingVoucher.id) setViewVoucher(null);
      } else {
        toast({ title: "Cancellation Error", description: res.error, variant: "destructive" });
      }
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header with Quick Action Bar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-primary" />
            <span>Vouchers & Accounting Journal</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Complete double-entry accounting engine for Contra, Payments, Receipts, Journals, Invoices, and Inventory.
          </p>
        </div>

        {/* Primary Action Buttons */}
        <div className="flex items-center flex-wrap gap-2">
          {/* F4: Contra */}
          <ContraVoucherForm
            isOpen={contraOpen}
            onOpenChange={setContraOpen}
            trigger={
              <Button
                variant={activeTab === "contra" ? "default" : "outline"}
                size="sm"
                className="gap-1.5 text-xs font-semibold"
                onClick={() => setContraOpen(true)}
              >
                <ArrowRightLeft className="h-3.5 w-3.5 text-accent" />
                <span>Contra (F4)</span>
              </Button>
            }
          />

          {/* F5: Payment */}
          <PaymentVoucherForm
            isOpen={paymentOpen}
            onOpenChange={setPaymentOpen}
            trigger={
              <Button
                variant={activeTab === "payment" ? "default" : "outline"}
                size="sm"
                className="gap-1.5 text-xs font-semibold"
                onClick={() => setPaymentOpen(true)}
              >
                <CreditCard className="h-3.5 w-3.5 text-destructive" />
                <span>Payment (F5)</span>
              </Button>
            }
          />

          {/* F6: Receipt */}
          <ReceiptVoucherForm
            isOpen={receiptOpen}
            onOpenChange={setReceiptOpen}
            trigger={
              <Button
                variant={activeTab === "receipt" ? "default" : "outline"}
                size="sm"
                className="gap-1.5 text-xs font-semibold"
                onClick={() => setReceiptOpen(true)}
              >
                <IndianRupee className="h-3.5 w-3.5 text-success" />
                <span>Receipt (F6)</span>
              </Button>
            }
          />

          {/* F7: Journal Entry */}
          <VoucherEntryForm
            type="Journal"
            trigger={
              <Button
                variant={activeTab === "journal" ? "default" : "outline"}
                size="sm"
                className="gap-1.5 text-xs font-semibold"
              >
                <Layers className="h-3.5 w-3.5 text-primary" />
                <span>Journal (F7)</span>
              </Button>
            }
          />

          {/* Inventory Vouchers */}
          <InventoryVoucherForm
            type="Stock Journal"
            isOpen={stockJournalOpen}
            onOpenChange={setStockJournalOpen}
            trigger={
              <Button variant="outline" size="sm" className="gap-1.5 text-xs">
                <Package className="h-3.5 w-3.5" />
                <span>Stock Journal</span>
              </Button>
            }
          />

          {/* Sales GST Invoice */}
          <GSTInvoiceForm
            type="Sales"
            trigger={
              <Button size="sm" className="bg-primary text-primary-foreground gap-1.5 text-xs font-semibold">
                <Plus className="h-3.5 w-3.5" />
                <span>New Invoice</span>
              </Button>
            }
          />
        </div>
      </div>

      {/* Voucher Category Navigation Tabs */}
      <div className="overflow-x-auto pb-1">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="bg-card border border-border p-1 h-auto flex flex-wrap gap-1">
            <TabsTrigger value="all" className="text-xs px-3 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              All Vouchers ({vouchers.length})
            </TabsTrigger>
            <TabsTrigger value="contra" className="text-xs px-3 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Contra ({vouchers.filter((v) => v.type === "Contra").length})
            </TabsTrigger>
            <TabsTrigger value="payment" className="text-xs px-3 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Payment ({vouchers.filter((v) => v.type === "Payment").length})
            </TabsTrigger>
            <TabsTrigger value="receipt" className="text-xs px-3 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Receipt ({vouchers.filter((v) => v.type === "Receipt").length})
            </TabsTrigger>
            <TabsTrigger value="journal" className="text-xs px-3 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Journal ({vouchers.filter((v) => v.type === "Journal").length})
            </TabsTrigger>
            <TabsTrigger value="sales" className="text-xs px-3 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Sales ({vouchers.filter((v) => v.type === "Sales").length})
            </TabsTrigger>
            <TabsTrigger value="purchase" className="text-xs px-3 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Purchase ({vouchers.filter((v) => v.type === "Purchase").length})
            </TabsTrigger>
            <TabsTrigger value="credit-note" className="text-xs px-3 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Credit Note ({vouchers.filter((v) => v.type === "Credit Note").length})
            </TabsTrigger>
            <TabsTrigger value="debit-note" className="text-xs px-3 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Debit Note ({vouchers.filter((v) => v.type === "Debit Note").length})
            </TabsTrigger>
            <TabsTrigger value="stock-journal" className="text-xs px-3 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Stock Journal ({vouchers.filter((v) => v.type === "Stock Journal").length})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Filter Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-3 rounded-lg bg-card border border-border">
        {/* Search Input */}
        <div className="flex items-center gap-2 bg-muted/40 border border-border rounded-md px-3 py-1.5 flex-1 max-w-sm">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by voucher #, party, narration, UTR..."
            className="bg-transparent text-xs outline-none w-full text-foreground placeholder:text-muted-foreground"
          />
        </div>

        {/* Date Range Filters */}
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground text-[11px]">From:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-8 px-2 rounded border border-border bg-card text-xs font-mono"
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground text-[11px]">To:</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-8 px-2 rounded border border-border bg-card text-xs font-mono"
            />
          </div>
          {(startDate || endDate || search) && (
            <button
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setSearch("");
              }}
              className="text-[11px] text-primary hover:underline px-1"
            >
              Reset
            </button>
          )}

          <div className="text-muted-foreground font-mono pl-2 border-l border-border">
            {filteredVouchers.length} {filteredVouchers.length === 1 ? "voucher" : "vouchers"}
          </div>
        </div>
      </div>

      {/* Vouchers Table */}
      <div className="rounded-lg bg-card border border-border shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b border-border text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">
                <th className="py-2.5 px-3 text-left w-8"></th>
                <th className="py-2.5 px-3 text-left">Voucher No</th>
                <th className="py-2.5 px-3 text-left">Date</th>
                <th className="py-2.5 px-3 text-left">Type</th>
                <th className="py-2.5 px-3 text-left">Party / Accounts</th>
                <th className="py-2.5 px-3 text-left">Mode / Ref</th>
                <th className="py-2.5 px-3 text-right">Debit (₹)</th>
                <th className="py-2.5 px-3 text-right">Credit (₹)</th>
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filteredVouchers.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-muted-foreground text-xs">
                    No vouchers found matching your filters.
                  </td>
                </tr>
              ) : (
                filteredVouchers.map((v) => {
                  const isExpanded = expandedId === v.id;
                  const statusInfo = STATUS_ICONS[v.status] || STATUS_ICONS.Draft;
                  const StatusIcon = statusInfo.icon;
                  const isCancelled = v.status === "Cancelled";

                  return (
                    <Fragment key={v.id}>
                      <tr
                        className={`hover:bg-muted/30 transition-colors text-xs ${
                          isCancelled ? "opacity-60 bg-muted/10" : ""
                        }`}
                      >
                        <td className="py-2.5 px-3">
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : v.id)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </button>
                        </td>
                        <td className="py-2.5 px-3 font-mono font-medium text-[#2563EB] dark:text-blue-400 hover:text-blue-700 transition-colors">
                          {v.voucherNumber}
                        </td>
                        <td className="py-2.5 px-3 font-mono text-muted-foreground whitespace-nowrap">
                          {v.date}
                          {v.isBackdated ? (
                            <span className="ml-1 text-[9px] px-1 py-0.2 rounded bg-amber-500/10 text-amber-600 font-semibold">
                              Backdated
                            </span>
                          ) : null}
                        </td>
                        <td className="py-2.5 px-3">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20">
                            {v.type}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 max-w-[200px] truncate">
                          <span className="font-medium text-foreground">{v.partyName || v.narration || "General Journal"}</span>
                        </td>
                        <td className="py-2.5 px-3 text-[11px] text-muted-foreground font-mono">
                          {v.paymentMode || (v.instrumentNo ? `Ref: ${v.instrumentNo}` : "—")}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono font-medium text-foreground">
                          ₹{v.totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono font-medium text-foreground">
                          ₹{v.totalCredit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusInfo.className}`}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {v.status}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => setViewVoucher(v)}
                              title="View Details"
                              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                            {!isCancelled && (role === "company_admin" || role === "ca") && (
                              <button
                                onClick={() => setCancellingVoucher(v)}
                                title="Cancel Voucher (Reverse Postings)"
                                className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded View with Double-Entry Breakdown */}
                      {isExpanded && (
                        <tr className="bg-muted/20 border-b border-border/80">
                          <td colSpan={10} className="p-3 pl-10">
                            <div className="space-y-2">
                              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center justify-between">
                                <span>Double-Entry Postings for {v.voucherNumber}</span>
                                {v.narration && (
                                  <span className="font-normal normal-case text-foreground italic">
                                    "{v.narration}"
                                  </span>
                                )}
                              </div>
                              <div className="bg-card rounded border border-border overflow-hidden">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-muted/40 text-[10px] text-muted-foreground font-semibold">
                                      <th className="py-1.5 px-3 text-left">Ledger Account</th>
                                      <th className="py-1.5 px-3 text-right">Debit (Dr)</th>
                                      <th className="py-1.5 px-3 text-right">Credit (Cr)</th>
                                      <th className="py-1.5 px-3 text-left">Line Remarks</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-border/40 font-mono">
                                    {v.lines.map((l) => (
                                      <tr key={l.id}>
                                        <td className="py-1.5 px-3 font-medium text-foreground">{l.ledgerName}</td>
                                        <td className="py-1.5 px-3 text-right">
                                          {l.debit > 0 ? `₹${l.debit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}
                                        </td>
                                        <td className="py-1.5 px-3 text-right">
                                          {l.credit > 0 ? `₹${l.credit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}
                                        </td>
                                        <td className="py-1.5 px-3 text-muted-foreground font-sans">{l.narration || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              {/* Allocations Info if present */}
                              {v.allocations && v.allocations.length > 0 && (
                                <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                                  <span className="font-semibold text-foreground">Allocated Invoices:</span>
                                  {v.allocations.map((a, i) => (
                                    <span key={i} className="px-1.5 py-0.5 rounded bg-muted font-mono">
                                      {a.invoiceNumber} (₹{a.allocatedAmount.toLocaleString("en-IN")})
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cancellation Confirmation Dialog */}
      {cancellingVoucher && (
        <Dialog open={!!cancellingVoucher} onOpenChange={(o) => !o && setCancellingVoucher(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive font-display">
                <RotateCcw className="h-5 w-5" />
                <span>Cancel & Reverse Voucher</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2 text-xs">
              <p className="text-muted-foreground">
                You are about to cancel voucher <strong className="text-foreground">{cancellingVoucher.voucherNumber}</strong> dated{" "}
                <strong className="text-foreground">{cancellingVoucher.date}</strong> for{" "}
                <strong className="text-foreground">₹{cancellingVoucher.totalDebit.toLocaleString("en-IN")}</strong>.
              </p>
              <div className="p-2.5 rounded bg-destructive/10 border border-destructive/20 text-destructive text-[11px]">
                ⚠️ In compliance with statutory accounting rules, cancelling this voucher will preserve its audit trail while automatically reversing all associated ledger balances and invoice allocations.
              </div>
              <div>
                <label className="font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                  Cancellation Reason *
                </label>
                <Input
                  type="text"
                  placeholder="e.g. Entry recorded in error / customer cancelled order"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="h-9 text-xs"
                  required
                />
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button variant="outline" size="sm" onClick={() => setCancellingVoucher(null)} disabled={cancelling}>
                Close
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancelSubmit}
                disabled={cancelling || !cancelReason.trim()}
                className="gap-1.5"
              >
                {cancelling ? "Reversing..." : "Confirm Cancellation & Reversal"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* View Voucher Details Modal */}
      {viewVoucher && (
        <Dialog open={!!viewVoucher} onOpenChange={(o) => !o && setViewVoucher(null)}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between font-display text-base">
                <span>Voucher Details: {viewVoucher.voucherNumber}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-mono">
                  {viewVoucher.type}
                </span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2 text-xs">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 rounded-lg bg-muted/40 border border-border">
                <div>
                  <span className="text-muted-foreground block text-[10px] uppercase">Transaction Date</span>
                  <span className="font-mono font-medium text-foreground">{viewVoucher.date}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[10px] uppercase">System Entry Time</span>
                  <span className="font-mono text-muted-foreground text-[11px]">
                    {viewVoucher.createdAt ? new Date(viewVoucher.createdAt).toLocaleString("en-IN") : "—"}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[10px] uppercase">Payment Mode</span>
                  <span className="font-medium text-foreground">{viewVoucher.paymentMode || "General"}</span>
                </div>
                {viewVoucher.instrumentNo && (
                  <div>
                    <span className="text-muted-foreground block text-[10px] uppercase">Instrument / Ref #</span>
                    <span className="font-mono text-foreground">{viewVoucher.instrumentNo}</span>
                  </div>
                )}
                {viewVoucher.bankName && (
                  <div>
                    <span className="text-muted-foreground block text-[10px] uppercase">Bank Account</span>
                    <span className="text-foreground">{viewVoucher.bankName}</span>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground block text-[10px] uppercase">Status</span>
                  <span className="font-semibold text-foreground">{viewVoucher.status}</span>
                </div>
              </div>

              {/* Lines */}
              <div className="border border-border rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted text-[10px] text-muted-foreground font-semibold">
                      <th className="py-2 px-3 text-left">Ledger</th>
                      <th className="py-2 px-3 text-right">Debit (Dr)</th>
                      <th className="py-2 px-3 text-right">Credit (Cr)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border font-mono">
                    {viewVoucher.lines.map((l) => (
                      <tr key={l.id}>
                        <td className="py-2 px-3 font-medium text-foreground">{l.ledgerName}</td>
                        <td className="py-2 px-3 text-right">
                          {l.debit > 0 ? `₹${l.debit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {l.credit > 0 ? `₹${l.credit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-muted/40 font-bold">
                      <td className="py-2 px-3 text-foreground">Total</td>
                      <td className="py-2 px-3 text-right text-foreground">
                        ₹{viewVoucher.totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 px-3 text-right text-foreground">
                        ₹{viewVoucher.totalCredit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {viewVoucher.narration && (
                <div className="p-2.5 rounded bg-muted/20 border border-border">
                  <span className="font-semibold text-muted-foreground uppercase text-[10px] block mb-0.5">
                    Narration:
                  </span>
                  <p className="text-foreground italic">{viewVoucher.narration}</p>
                </div>
              )}
            </div>
            <DialogFooter className="pt-2">
              <Button variant="outline" size="sm" onClick={() => setViewVoucher(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
